import { useEffect, useRef, useState } from 'react'
import { useLocation } from 'react-router-dom'
import { FileText, HelpCircle, Loader2, Paperclip, Send, X } from 'lucide-react'
import { useAccount } from '@/contexts/AccountContext'
import { useUserProfile } from '@/hooks/useSupabaseAuth'
import { useToast } from '@/hooks/use-toast'
import { requestHelp, type RequestHelpAttachment } from '@/lib/requestHelp'
import { Button } from '@/components/ui/button'
import { Textarea } from '@/components/ui/textarea'
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from '@/components/ui/dialog'

/** Allowlisted attachment MIME types — re-validated server-side. */
const ACCEPTED_TYPES = [
  'image/png',
  'image/jpeg',
  'image/webp',
  'image/gif',
  'application/pdf',
]
/** `accept` attribute: extensions + MIME types for the widest browser support. */
const ACCEPT_ATTR =
  '.png,.jpg,.jpeg,.webp,.gif,.pdf,image/png,image/jpeg,image/webp,image/gif,application/pdf'
const MAX_FILES = 3
const MAX_FILE_BYTES = 5 * 1024 * 1024

/** A selected file plus an object URL for image previews (null for PDFs). */
interface SelectedAttachment {
  file: File
  previewUrl: string | null
}

function formatBytes(bytes: number): string {
  if (bytes < 1024) return `${bytes} B`
  if (bytes < 1024 * 1024) return `${Math.round(bytes / 1024)} KB`
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`
}

/**
 * Read a File as base64, stripping the `data:<type>;base64,` prefix so only the
 * raw payload is sent. Resend (server-side) wants the bare base64 string.
 */
function fileToBase64(file: File): Promise<string> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader()
    reader.onload = () => {
      const result = typeof reader.result === 'string' ? reader.result : ''
      const comma = result.indexOf(',')
      resolve(comma >= 0 ? result.slice(comma + 1) : result)
    }
    reader.onerror = () => reject(reader.error ?? new Error('Failed to read file'))
    reader.readAsDataURL(file)
  })
}

interface GetHelpDialogProps {
  /** Optional override for the trigger Button (e.g. icon-only on mobile). */
  variant?: 'outline' | 'ghost'
  /** Hide the "Get Help" label, showing only the icon (mobile menu). */
  iconOnly?: boolean
  className?: string
}

/**
 * Global "Get Help" entry point (CML-183). Opens a dialog where any signed-in
 * user can describe a problem; on Send we POST to the `request-help` Edge
 * Function, which emails a support ticket and best-effort files a Linear issue.
 *
 * Context is captured automatically — caller email (from auth), display name
 * (profile metadata), account name (workspace), and the current page — so the
 * support ticket has everything needed without the user filling out fields.
 */
export function GetHelpDialog({ variant = 'outline', iconOnly = false, className }: GetHelpDialogProps) {
  const { account } = useAccount()
  const { data: profile } = useUserProfile()
  const { toast } = useToast()
  const location = useLocation()

  const [open, setOpen] = useState(false)
  const [message, setMessage] = useState('')
  const [sending, setSending] = useState(false)
  const [attachments, setAttachments] = useState<SelectedAttachment[]>([])
  const [attachmentError, setAttachmentError] = useState<string | null>(null)
  const fileInputRef = useRef<HTMLInputElement>(null)

  // Revoke any outstanding object URLs on unmount (a ref avoids re-running the
  // cleanup on every attachment change, which would revoke still-visible URLs).
  const attachmentsRef = useRef(attachments)
  attachmentsRef.current = attachments
  useEffect(
    () => () => {
      for (const a of attachmentsRef.current) {
        if (a.previewUrl) URL.revokeObjectURL(a.previewUrl)
      }
    },
    [],
  )

  const handleFiles = (selected: FileList | null) => {
    if (!selected || selected.length === 0) return
    const errors: string[] = []
    const accepted: SelectedAttachment[] = []
    const room = MAX_FILES - attachments.length

    for (const file of Array.from(selected)) {
      if (accepted.length >= room) {
        errors.push(`You can attach at most ${MAX_FILES} files.`)
        break
      }
      if (!ACCEPTED_TYPES.includes(file.type)) {
        errors.push(`${file.name}: unsupported file type.`)
        continue
      }
      if (file.size > MAX_FILE_BYTES) {
        errors.push(`${file.name}: exceeds the 5MB limit.`)
        continue
      }
      accepted.push({
        file,
        previewUrl: file.type.startsWith('image/') ? URL.createObjectURL(file) : null,
      })
    }

    if (accepted.length) setAttachments((prev) => [...prev, ...accepted])
    setAttachmentError(errors.length ? Array.from(new Set(errors)).join(' ') : null)
  }

  const removeAttachment = (index: number) => {
    setAttachments((prev) => {
      const next = [...prev]
      const [removed] = next.splice(index, 1)
      if (removed?.previewUrl) URL.revokeObjectURL(removed.previewUrl)
      return next
    })
    setAttachmentError(null)
  }

  const clearAttachments = () => {
    setAttachments((prev) => {
      for (const a of prev) {
        if (a.previewUrl) URL.revokeObjectURL(a.previewUrl)
      }
      return []
    })
    setAttachmentError(null)
  }

  const displayName = (() => {
    const meta = profile?.metadata as { first_name?: string; last_name?: string } | undefined
    const name = [meta?.first_name, meta?.last_name].filter(Boolean).join(' ').trim()
    return name || undefined
  })()

  const handleSend = async () => {
    if (!message.trim() || sending) return
    setSending(true)
    try {
      // Encode each attachment to base64 (data: prefix stripped) before sending.
      let encoded: RequestHelpAttachment[] | undefined
      if (attachments.length > 0) {
        encoded = await Promise.all(
          attachments.map(async ({ file }) => ({
            filename: file.name,
            contentType: file.type,
            data: await fileToBase64(file),
          })),
        )
      }

      // Full URL is the source of truth for the ticket link; fall back to the
      // router pathname if the browser URL is somehow unavailable.
      const pageUrl = window.location.href || `${window.location.origin}${location.pathname}`
      await requestHelp({
        message: message.trim(),
        page_url: pageUrl,
        display_name: displayName,
        account_name: account?.name || undefined,
        attachments: encoded,
      })
      setMessage('')
      clearAttachments()
      setOpen(false)
      toast({
        variant: 'success',
        title: 'Your help request has been sent',
        description: 'We’ll get back to you shortly.',
      })
    } catch (err) {
      toast({
        variant: 'destructive',
        title: 'Failed to send help request',
        description: err instanceof Error ? err.message : 'Unknown error',
      })
    } finally {
      setSending(false)
    }
  }

  return (
    <>
      <Button
        variant={variant}
        size="sm"
        className={className}
        onClick={() => setOpen(true)}
      >
        <HelpCircle className={iconOnly ? 'h-4 w-4' : 'h-4 w-4 sm:mr-2'} />
        {!iconOnly && <span className="hidden sm:inline">Need help?</span>}
      </Button>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Get Help</DialogTitle>
            <DialogDescription>
              Report a bug, ask a question, or share feedback — our team is here to help. Your input helps us make iCrop better.
            </DialogDescription>
          </DialogHeader>

          <Textarea
            value={message}
            onChange={(e) => setMessage(e.target.value)}
            placeholder="What can we help you with?"
            rows={6}
            disabled={sending}
            autoFocus
          />

          <p className="text-xs text-muted-foreground">
            Need to send us a video or screen recording? We recommend{' '}
            <a
              href="https://www.loom.com"
              target="_blank"
              rel="noopener noreferrer"
              className="font-medium underline underline-offset-2"
            >
              Loom
            </a>{' '}
            (it’s free) — record it and paste the share link in your message so we can review.
          </p>

          {/* Attachments */}
          <div className="space-y-2">
            <input
              ref={fileInputRef}
              type="file"
              accept={ACCEPT_ATTR}
              multiple
              className="hidden"
              onChange={(e) => {
                handleFiles(e.target.files)
                // Reset so re-selecting the same file still fires onChange.
                e.target.value = ''
              }}
              disabled={sending}
            />
            <div className="flex flex-wrap items-center gap-2">
              <Button
                type="button"
                variant="ghost"
                size="sm"
                onClick={() => fileInputRef.current?.click()}
                disabled={sending || attachments.length >= MAX_FILES}
              >
                <Paperclip className="h-4 w-4 mr-2" />
                Attach files
              </Button>
              <span className="text-xs text-muted-foreground">
                PNG, JPEG, WEBP, GIF, or PDF · up to {MAX_FILES} files, 5MB each
              </span>
            </div>

            {attachmentError && (
              <p className="text-sm text-red-600">{attachmentError}</p>
            )}

            {attachments.length > 0 && (
              <ul className="space-y-2">
                {attachments.map((a, i) => (
                  <li
                    key={`${a.file.name}-${i}`}
                    className="flex items-center gap-2 rounded-md border px-2 py-1.5 text-sm"
                  >
                    {a.previewUrl ? (
                      <img
                        src={a.previewUrl}
                        alt=""
                        className="h-8 w-8 shrink-0 rounded object-cover"
                      />
                    ) : (
                      <FileText className="h-8 w-8 shrink-0 p-1.5 text-muted-foreground" />
                    )}
                    <span className="flex-1 truncate">{a.file.name}</span>
                    <span className="shrink-0 text-xs text-muted-foreground">
                      {formatBytes(a.file.size)}
                    </span>
                    <button
                      type="button"
                      onClick={() => removeAttachment(i)}
                      disabled={sending}
                      aria-label={`Remove ${a.file.name}`}
                      className="shrink-0 rounded p-1 text-muted-foreground hover:bg-muted hover:text-foreground disabled:opacity-50"
                    >
                      <X className="h-4 w-4" />
                    </button>
                  </li>
                ))}
              </ul>
            )}
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setOpen(false)} disabled={sending}>
              Cancel
            </Button>
            <Button onClick={handleSend} disabled={!message.trim() || sending}>
              {sending ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Send className="h-4 w-4 mr-2" />}
              Send
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  )
}
